import React, { useState, useEffect, useRef } from 'react';

const TextReveal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const toggleTextReveal = () => {
        setIsOpen(!isOpen);
    };

    const styles = {
        textRevealContainer: {
            position: 'relative',
            display: 'inline-block',
            width: '100%',
            marginTop: 50,
        },

        textRevealButton: {
            background: '#fff',
            color: '#333',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            width: '100%',
            justifyContent: 'space-between',
            transform: isOpen ? 'translateY(0)' : 'translateY(0)',
        },

        textRevealButtonHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        },

        textRevealArrow: {
            transition: 'transform 0.3s ease',
            fontSize: '12px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        },

        textRevealContent: {
            position: 'relative',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            opacity: isOpen ? 1 : 0,
            visibility: isOpen ? 'visible' : 'hidden',
            transform: isOpen ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.3s ease',
            marginTop: '8px',
            overflow: 'hidden',
            maxHeight: isOpen ? '1000px' : '0',
        },

        textContent: {
            padding: '20px',
            color: '#333',
            fontSize: '14px',
            lineHeight: '1.6',
            textAlign: 'justify',
            maxHeight: '300px',
            overflowY: 'auto',
            overflowX: 'hidden',
        },

        textContentP: {
            marginBottom: '15px',
        },

        textContentPLast: {
            marginBottom: '0',
        },
    };

    return (
        <div style={styles.textRevealContainer} ref={containerRef}>
            <button
                style={styles.textRevealButton}
                onClick={toggleTextReveal}
                onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                }}
            >
                <span>Rules for the translation key</span>
                <span style={styles.textRevealArrow}>▼</span>
            </button>

            <div style={styles.textRevealContent}>
                <div style={styles.textContent}>
                    <p style={styles.textContentP}>
                        Keys should always be structured so that they have at
                        LVL_ONE the macrocategory, at LVL_DUE the section (e.g.
                        PROGRAMS, DASHBOARD, etc.), at LVL_TRE the reference for
                        which the key was created (e.g. OK_GOT_IT, BAKERY,
                        COOKING, etc.) e.g. GLOBALS.GENERAL.OK_GOT_IT,
                        OV.COMMANDS.COOKING, OV.PROGRAMS.BAKERY
                    </p>

                    <p style={styles.textContentP}>
                        In addition, these are a number of mandatory rules to be
                        followed in order to avoid errors and to ensure easy
                        readability of the translation keys: <br></br>
                        <strong>
                            Keys must always be third level (never more and
                            never less)
                        </strong>{' '}
                        <br></br> Each “.” equals a graft level, so third level
                        means 2 points: ‘OV.PROGRAMS.BAKERY’ <br></br> N.B. the
                        levels must never be more than 3 because otherwise they
                        will not work in the app, they can be less than 3 but it
                        is not recommended, the rule we define is that new keys
                        must always be third level. **
                    </p>

                    <p style={styles.textContentP}>
                        <strong>Do not create multiple keys </strong> <br></br>
                        If we need the text ‘OK, got it.’, and a key with that
                        text already exists, we should not create a new one but
                        reuse the same one
                    </p>

                    <p style={styles.textContentPLast}>
                        <strong>Formatting of text</strong> <br></br>
                        <ul>
                            <li>Keys are not created with empty text</li>
                            <li>Keys are not created with empty text</li>
                            <li>Keys are only created with English text</li>
                            <li>
                                Text must never be in upper case (e.g. if you
                                need all-caps text for a button, you put it in
                                caps directly from the app)
                            </li>
                        </ul>
                    </p>
                    <p style={styles.textContentPLast}>
                        <strong>Formatting of keys</strong> <br></br>
                        <ul>
                            <li>
                                Formatting of keys Special characters other than
                                “_” are not allowed in keys. If you want to use
                                more than one word for a level you must
                                do:OV.PROGRAMS.RED_MEAT
                            </li>
                            <li>They all have to be capitalised</li>
                        </ul>
                    </p>
                    <p style={styles.textContentPLast}>
                        <strong>Dynamic Texts</strong> <br></br>
                        {`Sometimes it is
                        necessary to have a dynamic value within a text, e.g.
                        “You have added n foods to the inventory”. In this case
                        that n should take the value %{VariableName}So the
                        string will become: ‘You have added %{VariableName} food
                        to the inventory’. N.B. the variable name must be in
                        camelCase and in English (let's give it a meaningful
                        name, for the example above we could use for example
                        foodsNumber)`}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TextReveal;
